import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useServices } from '../context/ServiceContext';

const { FiStar, FiMapPin, FiPhone, FiMail, FiClock, FiCheck, FiArrowLeft, FiImage } = FiIcons;

const ProviderProfile = () => {
  const { id } = useParams();
  const { getServiceById } = useServices();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedImage, setSelectedImage] = useState(0);
  
  const service = getServiceById(id);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Service not found</h2>
          <p className="text-gray-600 mb-4">The service you're looking for doesn't exist.</p>
          <Link
            to="/search"
            className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Browse Services
          </Link>
        </div>
      </div>
    );
  }

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <SafeIcon key={i} icon={FiStar} className="text-yellow-400 fill-current" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <SafeIcon key="half" icon={FiStar} className="text-yellow-400 fill-current opacity-50" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <SafeIcon key={`empty-${i}`} icon={FiStar} className="text-gray-300" />
      );
    }

    return stars;
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'services', label: 'Services' },
    { id: 'reviews', label: 'Reviews' },
    { id: 'gallery', label: 'Gallery' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link
          to="/search"
          className="inline-flex items-center text-gray-600 hover:text-primary-600 mb-6 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2" />
          Back to Search
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Header */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <div className="flex flex-col md:flex-row md:items-start gap-6">
                <img
                  src={service.image}
                  alt={service.name}
                  className="w-full md:w-32 h-32 object-cover rounded-lg"
                />
                
                <div className="flex-1">
                  <div className="flex flex-col md:flex-row md:items-start justify-between mb-4">
                    <div>
                      <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        {service.name}
                      </h1>
                      <span className="bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm font-medium">
                        {service.category}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center mb-3">
                    <div className="flex items-center mr-3">
                      {renderStars(service.rating)}
                    </div>
                    <span className="text-lg font-semibold text-gray-900">
                      {service.rating}
                    </span>
                    <span className="text-gray-500 ml-2">
                      ({service.reviewCount} reviews)
                    </span>
                  </div>

                  <div className="flex items-center text-gray-600 mb-4">
                    <SafeIcon icon={FiMapPin} className="mr-2" />
                    <span>{service.location}</span>
                  </div>

                  <p className="text-gray-700">
                    {service.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-sm mb-6">
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === tab.id
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {activeTab === 'overview' && (
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      About {service.name}
                    </h3>
                    <p className="text-gray-700 mb-6">
                      {service.description}
                    </p>
                    
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">
                      Service Areas
                    </h4>
                    <p className="text-gray-700">
                      Serving {service.location} and surrounding areas
                    </p>
                  </div>
                )}

                {activeTab === 'services' && (
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      Services Offered
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {service.services.map((serviceItem, index) => (
                        <div
                          key={index}
                          className="flex items-center p-4 bg-gray-50 rounded-lg"
                        >
                          <SafeIcon icon={FiCheck} className="text-green-500 mr-3" />
                          <span className="font-medium text-gray-900">
                            {serviceItem}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'reviews' && (
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      Customer Reviews
                    </h3>
                    <div className="space-y-6">
                      {service.reviews.map((review) => (
                        <div key={review.id} className="border-b border-gray-200 pb-6 last:border-b-0">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                                <span className="text-primary-600 font-semibold">
                                  {review.author.charAt(0)}
                                </span>
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900">
                                  {review.author}
                                </h4>
                                <div className="flex items-center">
                                  {renderStars(review.rating)}
                                </div>
                              </div>
                            </div>
                            <span className="text-sm text-gray-500">
                              {new Date(review.date).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-gray-700">
                            {review.comment}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'gallery' && (
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      Work Gallery
                    </h3>
                    {service.gallery && service.gallery.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {service.gallery.map((image, index) => (
                          <div
                            key={index}
                            className="relative group cursor-pointer"
                            onClick={() => setSelectedImage(index)}
                          >
                            <img
                              src={image}
                              alt={`Work sample ${index + 1}`}
                              className="w-full h-40 object-cover rounded-lg group-hover:opacity-90 transition-opacity"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center">
                              <SafeIcon icon={FiImage} className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-2xl" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <SafeIcon icon={FiImage} className="text-4xl text-gray-300 mx-auto mb-2" />
                        <p className="text-gray-500">No gallery images available</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Contact Information
              </h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-center">
                  <SafeIcon icon={FiPhone} className="text-primary-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <a
                      href={`tel:${service.phone}`}
                      className="text-gray-900 hover:text-primary-600 transition-colors"
                    >
                      {service.phone}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <SafeIcon icon={FiMail} className="text-primary-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <a
                      href={`mailto:${service.email}`}
                      className="text-gray-900 hover:text-primary-600 transition-colors"
                    >
                      {service.email}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <SafeIcon icon={FiClock} className="text-primary-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Response Time</p>
                    <p className="text-gray-900">Usually within 2 hours</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <button className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors">
                  Get Quote
                </button>
                <button className="w-full border border-primary-600 text-primary-600 py-3 px-4 rounded-lg font-semibold hover:bg-primary-50 transition-colors">
                  Message
                </button>
                <a
                  href={`tel:${service.phone}`}
                  className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center"
                >
                  <SafeIcon icon={FiPhone} className="mr-2" />
                  Call Now
                </a>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">
                  Why choose {service.name}?
                </h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center">
                    <SafeIcon icon={FiCheck} className="text-green-500 mr-2" />
                    Licensed & Insured
                  </li>
                  <li className="flex items-center">
                    <SafeIcon icon={FiCheck} className="text-green-500 mr-2" />
                    Background Checked
                  </li>
                  <li className="flex items-center">
                    <SafeIcon icon={FiCheck} className="text-green-500 mr-2" />
                    Satisfaction Guaranteed
                  </li>
                  <li className="flex items-center">
                    <SafeIcon icon={FiCheck} className="text-green-500 mr-2" />
                    Free Estimates
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderProfile;